import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, Trophy } from "lucide-react";
import Layout from "@/components/Layout";
import Composer from "@/components/Composer";
import PostCard from "@/components/PostCard";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Link } from "react-router-dom";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "income_proof", label: "Income Proofs" },
  { key: "regular", label: "Discussion" },
];

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [leaders, setLeaders] = useState([]);
  const [suggested, setSuggested] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: f }, { data: l }, { data: s }] = await Promise.all([
        api.get("/posts/feed"),
        api.get("/leaderboard?timeframe=week"),
        api.get("/users/suggested"),
      ]);
      setPosts(f);
      setLeaders(l.slice(0, 5));
      setSuggested(s.slice(0, 4));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = filter === "all" ? posts : posts.filter((p) => (p.post_type || "regular") === filter);

  const follow = async (uid) => {
    await api.post(`/users/${uid}/follow`);
    setSuggested((prev) => prev.filter((u) => u.user_id !== uid));
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-8 px-4 md:px-8 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
        <div className="max-w-2xl">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h1 className="font-heading font-bold text-4xl tracking-tight">Feed</h1>
              <p className="text-sm text-white/45 font-mono mt-1">What's working right now</p>
            </div>
          </div>

          <div className="surface rounded-xl p-2 mb-5 flex items-center gap-1" data-testid="feed-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition ${
                  filter === f.key ? "bg-white/10 text-white" : "text-white/45 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <Composer onPosted={(p) => setPosts((prev) => [p, ...prev])} />

          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="surface rounded-xl h-44 shimmer" />)}</div>
          ) : visible.length === 0 ? (
            <div className="surface rounded-xl p-12 text-center">
              <TrendingUp size={32} className="text-[#00E676] mx-auto mb-3" strokeWidth={1.5} />
              <div className="font-heading font-bold text-xl mb-1">No posts yet.</div>
              <div className="text-sm text-white/50">Drop the first income proof or a tip.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {visible.map((p) => (
                <PostCard
                  key={p.post_id}
                  post={p}
                  onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.post_id !== id))}
                />
              ))}
            </div>
          )}
        </div>

        <aside className="hidden lg:flex flex-col gap-5 sticky top-8 self-start">
          <div className="surface rounded-xl p-5" data-testid="sidebar-leaders">
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={14} className="text-[#00E676]" />
              <div className="text-xs font-mono uppercase tracking-wider text-white/60">Top this week</div>
            </div>
            <div className="space-y-3">
              {leaders.length === 0 && <div className="text-xs text-white/30 font-mono">No income proofs yet.</div>}
              {leaders.map((r, i) => (
                <Link
                  key={r.user.user_id}
                  to={`/profile/${r.user.user_id}`}
                  className="flex items-center gap-3 hover:bg-white/[0.03] -mx-2 px-2 py-1.5 rounded-md transition"
                  data-testid={`leader-${r.user.user_id}`}
                >
                  <span className="font-mono text-xs text-white/40 w-4">{i + 1}</span>
                  <img
                    src={r.user.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${r.user.user_id}`}
                    alt=""
                    className="w-8 h-8 rounded-md object-cover bg-white/5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{r.user.name}</div>
                  </div>
                  <div className="font-mono text-sm text-[#00E676]">${(r.total || 0).toLocaleString()}</div>
                </Link>
              ))}
            </div>
            <Link
              to="/leaderboard"
              className="text-xs font-mono text-white/40 hover:text-[#00E676] mt-3 inline-block transition"
            >
              See full board →
            </Link>
          </div>

          <div className="surface rounded-xl p-5" data-testid="sidebar-suggested">
            <div className="text-xs font-mono uppercase tracking-wider text-white/60 mb-4">People to follow</div>
            <div className="space-y-3">
              {suggested.length === 0 && <div className="text-xs text-white/30 font-mono">No suggestions yet.</div>}
              {suggested.map((u) => (
                <div key={u.user_id} className="flex items-center gap-3" data-testid={`suggested-${u.user_id}`}>
                  <img
                    src={u.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${u.user_id}`}
                    alt=""
                    className="w-9 h-9 rounded-md object-cover bg-white/5"
                  />
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${u.user_id}`} className="font-semibold text-sm truncate hover:text-[#00E676] transition block">{u.name}</Link>
                    <div className="text-[11px] text-white/40 font-mono truncate">
                      ${(u.total_earnings || 0).toLocaleString()} earned
                    </div>
                  </div>
                  <button
                    data-testid={`follow-${u.user_id}`}
                    onClick={() => follow(u.user_id)}
                    className="text-xs font-semibold text-[#00E676] hover:text-black hover:bg-[#00E676] px-2.5 py-1 rounded-md border border-[#00E676]/30 transition"
                  >
                    Follow
                  </button>
                </div>
              ))}
            </div>
          </div>

          <Link
            to="/coach"
            data-testid="sidebar-coach-cta"
            className="surface rounded-xl p-5 card-hover cursor-pointer block"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-[#00E676]" />
              <div className="text-xs font-mono uppercase tracking-wider">AI Money Coach</div>
            </div>
            <p className="text-sm text-white/65 leading-snug mb-3">Tell it your goals. Get a step-by-step plan.</p>
            <span className="text-xs font-semibold text-[#00E676]">Start chat →</span>
          </Link>
        </aside>
      </div>
    </Layout>
  );
}
